export type ContentBlock = {
  type: 'heading' | 'heading3' | 'paragraph' | 'list' | 'callout' | 'formula'
  content?: string
  items?: string[]
}

export type ClientCase = {
  slug: string
  clientName: string
  sector: string
  shortDescription: string
  heroImage: string
  metaDescription: string
  content: ContentBlock[]
  website?: string
}

export const clientCases: ClientCase[] = [
  {
    slug: 'overnamehuys',
    clientName: 'Overnamehuys',
    sector: 'Financiële Dienstverlening',
    shortDescription:
      'Een nieuwe website voor Overnamehuys die de autoriteit van een marktleider uitstraalt: professioneel en toonaangevend, maar tegelijk persoonlijk en laagdrempelig.',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/meTvYzmyxaEQsgHqc2Xa7L/Myrte_Photography_DSC06532-3-NEWSDETAIL.jpg',
    metaDescription:
      'Lees hoe wij voor Overnamehuys, een van de koplopers in bedrijfsovernames in Nederland, een krachtige website bouwden die autoriteit en persoonlijkheid combineert.',
    content: [
      {
        type: 'paragraph',
        content:
          'Overnamehuys is een van de toonaangevende spelers op het gebied van bedrijfsovernames in Nederland. Ze begeleiden ondernemers bij het kopen en verkopen van bedrijven en voeren bedrijfsscans en prijsbeoordelingen uit. Als onderdeel van FCG Adviesgroep, een overkoepelende holding met meerdere gespecialiseerde labels, opereren ze in een markt waar vertrouwen, expertise en autoriteit alles bepalen.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'De bestaande website deed de positie van Overnamehuys in de markt geen recht. De uitstraling sloot niet aan bij het niveau van de dienstverlening: niet professioneel genoeg voor de zakelijke doelgroep, terwijl de klant juist wilde uitstralen dat ze de koploper zijn in hun sector. Tegelijkertijd moest de website menselijk en toegankelijk blijven, een moeilijke combinatie in een formele branche. Dat spanningsveld, marktleider én persoonlijk, was de kern van de designopgave.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Ontwikkeling van een kleurpalet en visuele stijl die autoriteit en professionaliteit uitstralen, zonder koud of afstandelijk te worden.',
          'Storytelling-structuur die de expertise van het team en de begeleidingsaanpak centraal zet, zodat bezoekers direct begrijpen wat Overnamehuys uniek maakt.',
          'Laagdrempelige contactstructuur en heldere calls-to-action die de stap naar een eerste gesprek zo klein mogelijk maken.',
          'Duidelijke presentatie van de diensten zoals begeleiding bij overname, bedrijfsscan en prijsbeoordeling, in begrijpelijke taal voor ondernemers.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een website die de marktpositie van Overnamehuys als koploper visueel en inhoudelijk onderbouwt.',
          'Een uitstraling die professioneel en toonaangevend is, maar tegelijk warm en persoonlijk overkomt.',
          'Een sterke digitale basis voor de verdere groei van het merk binnen de FCG Adviesgroep.',
        ],
      },
      {
        type: 'callout',
        content:
          'Een website die laat zien wie je bent voordat je ook maar één woord zegt. Dat is wat een marktleider nodig heeft.',
      },
      {
        type: 'paragraph',
        content:
          'Overnamehuys heeft nu een online aanwezigheid die recht doet aan hun positie in de markt en ondernemers direct het vertrouwen geeft om het gesprek aan te gaan.',
      },
    ],
  },
  {
    slug: 'daily-crew',
    clientName: 'Daily Crew',
    sector: 'Uitzendbureau',
    shortDescription:
      'Voor DailyCrew ontwikkelden we een strakke website die betrouwbaar personeel en werkgevers in schoonmaak, warehouse en bouw direct verbindt.',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/rAJH6sGb1tDyRSYtwEVkra/Akim_foto.jpg',
    metaDescription:
      'Lees hoe wij voor DailyCrew een conversiegerichte website ontwikkelden die werknemers en werkgevers in diverse sectoren effectief samenbrengt.',
    content: [
      {
        type: 'paragraph',
        content:
          'DailyCrew is een jonge bemiddelaar die vakmensen en werkgevers met elkaar verbindt. Sinds de oprichting in 2023 richten zij zich op drie sectoren: schoonmaak, warehouse en bouw. Ze leveren betrouwbaar en flexibel personeel dat direct inzetbaar is.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'Als startende onderneming had DailyCrew behoefte aan een sterke online aanwezigheid die direct vertrouwen uitstraalt. De website moest twee verschillende doelgroepen aanspreken: werkzoekenden die een betrouwbare werkgever zoeken, en bedrijven die snel flexibel personeel nodig hebben.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Ontwikkeling van een heldere websitestructuur met gescheiden paden voor werkgevers en werknemers.',
          'Implementatie van snelle en laagdrempelige inschrijfformulieren om de conversie te verhogen.',
          'Een modern en no-nonsense design dat past bij de hands-on mentaliteit van de sectoren waarin ze opereren.',
          'Optimalisatie voor mobiel gebruik, aangezien de meeste werkzoekenden via hun telefoon navigeren.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een professionele uitstraling die direct vertrouwen wekt bij grote opdrachtgevers.',
          'Een gestroomlijnd proces voor nieuwe inschrijvingen, wat de administratieve last verlaagt.',
          'Verhoogde zichtbaarheid binnen de specifieke niches van schoonmaak, warehouse en bouw.',
          'Een grote opdrachtgever binnengehaald die mede dankzij de professionele website contact opnam.',
        ],
      },
      {
        type: 'heading3',
        content: 'Bewijs uit de praktijk: een grote klant via de website',
      },
      {
        type: 'paragraph',
        content:
          'Kort na de livegang heeft DailyCrew een grote opdrachtgever binnengehaald die rechtstreeks via de website contact opnam. De klant gaf expliciet aan dat de professionele uitstraling van de site een doorslaggevende reden was om het gesprek aan te gaan. Een mooi voorbeeld van hoe een doordachte online presentatie direct doorvertaalt naar concrete groei.',
      },
      {
        type: 'callout',
        content:
          'Een website die niet alleen informeert, maar actief bijdraagt aan de groei van het bestand aan vakmensen en opdrachtgevers, én aan het binnenhalen van grote opdrachtgevers.',
      },
      {
        type: 'paragraph',
        content:
          'Met deze nieuwe digitale basis is DailyCrew klaar om hun positie in de markt verder te verstevigen en hun belofte van korte lijnen en persoonlijke aandacht online waar te maken.',
      },
    ],
  },
  {
    slug: 'fcg-adviesgroep',
    clientName: 'FCG Adviesgroep',
    sector: 'Financiële Dienstverlening',
    shortDescription:
      'Een overzichtelijk en representatief platform voor FCG Adviesgroep dat alle labels onder één dak samenbrengt: helder, professioneel en zonder rommel.',
    heroImage:
      'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=500&fit=crop',
    metaDescription:
      'Lees hoe wij voor FCG Adviesgroep, de overkoepelende holding van Overnamehuys, Mijn Bedrijfswaarderingen en Financieringsgilde, een strakke en representatieve website bouwden na een technische migratiecalamiteit.',
    content: [
      {
        type: 'paragraph',
        content:
          'FCG Adviesgroep is de overkoepelende holding van meerdere gespecialiseerde labels binnen de financiële dienstverlening, waaronder Overnamehuys, Mijn Bedrijfswaarderingen en Financieringsgilde. De groep is een van de koplopers in haar sector in Nederland. FCG zelf heeft geen eigen dienst. Het is het gezicht van de groep als geheel, de plek waar alle merken samenkomen.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'Tijdens de migratie van de bestaande FCG-website naar onze eigen hosting liep de overdracht technisch niet goed. De website integreerde niet correct, waardoor er snel ingegrepen moest worden. Noord Digital nam de verantwoordelijkheid en bouwde in korte tijd een volledig nieuwe website, zonder extra kosten voor de klant. Inhoudelijk was de opgave helder maar niet eenvoudig: alle labels van de groep moesten op één plek samenkomen, zonder dat het een onoverzichtelijke dump van informatie werd.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Snelle reconstructie van de website vanuit nul, binnen een strak tijdsbestek, om de continuïteit voor de klant te waarborgen.',
          'Heldere en overzichtelijke structuur die de verschillende labels presenteert zonder de pagina te overladen.',
          'Professionele uitstraling die past bij de positie van FCG als overkoepelende holding in een serieuze branche.',
          'Strakke visuele hiërarchie zodat bezoekers direct begrijpen hoe de groep is opgebouwd en waar ze naartoe moeten.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een volledig nieuwe website opgeleverd binnen korte tijd, ondanks de technische calamiteit.',
          'Een overzichtelijk en representatief platform dat alle labels van FCG Adviesgroep op waardige wijze presenteert.',
          'Een tevreden klant. De klant was blij met het eindresultaat, dat zijn verwachtingen overtrof gezien de omstandigheden.',
        ],
      },
      {
        type: 'callout',
        content:
          'Als het misgaat, los je het op. Zo simpel is het. En als het goed gaat, is de klant blij. Dat is het doel.',
      },
      {
        type: 'paragraph',
        content:
          'FCG Adviesgroep heeft nu een digitaal visitekaartje dat de omvang en professionaliteit van de groep recht doet, gebouwd op een solide technische basis op onze eigen hosting.',
      },
    ],
  },
  {
    slug: 'alex-construct',
    clientName: 'Alex Construct',
    sector: 'Renovatie en afbouw',
    shortDescription:
      'Voor Alex Construct bouwden we de eerste echte online aanwezigheid. Een strakke website die het vakmanschap zichtbaar maakt en grotere opdrachten dichterbij brengt.',
    heroImage:
      'https://images.unsplash.com/photo-1620626011761-996317b8d101?w=800&h=500&fit=crop',
    metaDescription:
      'Lees de case van Alex Construct. Een nieuwe website, hosting en zakelijke email zorgden voor een professionele uitstraling die grotere renovatieopdrachten oplevert.',
    content: [
      {
        type: 'paragraph',
        content:
          'Alex Construct is een renovatiespecialist in tegelwerk, badkamers, stucwerk en complete binnenrenovaties. De aanpak draait om één aanspreekpunt, korte communicatielijnen en strakke uitvoering, met meer dan honderd afgeronde projecten als basis. Wat je in de praktijk ziet, wilden ze ook online laten zien.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'Alex Construct kwam binnen zonder website. Het bedrijf draaide volledig op mond-tot-mondreclame en social media. Dat leverde mooie opdrachten op, maar maakte de groei afhankelijk van het bestaande netwerk. Voor grotere renovatieprojecten en zakelijke opdrachtgevers ontbrak een professioneel digitaal visitekaartje dat het niveau van het werk recht doet. Wie de naam googelde kwam letterlijk niets tegen.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Volledig nieuwe website opgezet vanaf nul, met strakke fotografie van afgeronde projecten als anker voor de uitstraling.',
          'Heldere dienstenstructuur waarin tegelwerk, vloeren, stucwerk, badkamerrenovatie en totaalrenovatie ieder hun eigen plek krijgen.',
          'De werkwijze in vier stappen op de site uitgelegd, zodat opdrachtgevers vooraf weten wat ze van het traject mogen verwachten.',
          'Eigen hosting en zakelijke email op het nieuwe domein opgeleverd, zodat alle communicatie naar opdrachtgevers professioneel staat ingericht.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een professionele online uitstraling die past bij het niveau van de uitvoering.',
          'Grotere opdrachtgevers die het bedrijf serieuzer nemen, omdat de online presentatie nu aansluit bij de kwaliteit van het werk.',
          'Een digitaal visitekaartje dat het verschil maakt in een markt waar veel renovatiebedrijven alleen op offerteportals zichtbaar zijn.',
        ],
      },
      {
        type: 'callout',
        content:
          'Vakmanschap zie je pas als het project is opgeleverd. Een goede website laat dat al zien voordat de eerste tegel ligt.',
      },
      {
        type: 'paragraph',
        content:
          'Alex Construct staat nu online net zo strak als hun renovaties eruitzien, en bereikt daarmee de opdrachtgevers waar ze eerder buiten beeld voor bleven.',
      },
    ],
    website: 'https://alexconstruct.nl',
  },
  {
    slug: 'gymfit-de-noord',
    clientName: 'GymFit de Noord',
    sector: 'Crossfit en sportschool',
    shortDescription:
      'De eerste online aanwezigheid voor crossfit gym GymFit de Noord. Inclusief gekoppeld ledeninschrijving en lessenrooster, zodat nieuwe sporters zich direct kunnen aanmelden voor een proefles.',
    heroImage:
      'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800&h=500&fit=crop',
    metaDescription:
      'Lees de case van GymFit de Noord. Een nieuwe website met geïntegreerde ledeninschrijving en rooster zorgde voor de eerste online aanwezigheid en directe aanmeldingen voor proeflessen.',
    content: [
      {
        type: 'paragraph',
        content:
          'GymFit de Noord is een crossfit gym in De Noord, in de gemeente Dijk en Waard. Het concept draait om intensieve groepslessen, kracht en conditie, in een kleine en gemotiveerde community waar iedereen elkaar bij naam kent. Dat persoonlijke karakter wilden ze ook online laten zien, zonder de drempel voor nieuwe sporters te verhogen.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'GymFit had nog geen eigen website. De gym was online alleen vindbaar via Facebook, wat voldoende leek voor de eerste leden uit het directe netwerk, maar te kort schoot voor mensen uit de bredere regio die op zoek waren naar een crossfit gym in de buurt. Ook het inschrijfproces en het lesrooster verliepen handmatig, wat tijd en aandacht kostte die bij de training hoort te liggen.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Volledig nieuwe website ontworpen en gebouwd met een energieke, no-nonsense uitstraling die past bij de crossfit cultuur.',
          'Lessenrooster en ledeninschrijving direct in de site geïntegreerd, zodat geïnteresseerden in één flow een proefles kunnen boeken.',
          'Lokale vindbaarheid opgezet, zodat zoekopdrachten als crossfit De Noord en sportschool Dijk en Waard bij GymFit uitkomen.',
          'Heldere uitleg van het concept, de proefles en de trainingstijden, zodat nieuwe sporters de drempel om binnen te lopen lager voelen.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een eerste serieuze online aanwezigheid voor GymFit de Noord die verder reikt dan de Facebook pagina.',
          'Nieuwe leden die zich rechtstreeks via de website inschrijven voor een proefles.',
          'Een soepeler inschrijfproces, waardoor de eigenaar zich kan richten op de training in plaats van op administratie.',
        ],
      },
      {
        type: 'callout',
        content:
          'Een crossfit gym leeft van energie en sfeer. De website moet die energie meteen overbrengen, voordat iemand de zaal binnenstapt.',
      },
      {
        type: 'paragraph',
        content:
          'Met de nieuwe site en gekoppelde ledeninschrijving heeft GymFit de Noord een digitale basis die meegroeit met de community en het ledenbestand.',
      },
    ],
  },
  {
    slug: 'mijn-bedrijfs-waarderingen',
    clientName: 'Mijn Bedrijfs Waarderingen',
    sector: 'Financiële Dienstverlening',
    shortDescription:
      'Een nieuw, zelfstandig label onder FCG Adviesgroep dat bedrijfswaardering als eigen propositie in de markt zet, met een slimme tool die adviseurs en Sebastiaan in de uitvoering verbindt.',
    heroImage:
      'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=500&fit=crop',
    metaDescription:
      'Lees de case van Mijn Bedrijfs Waarderingen. Een nieuw label binnen FCG Adviesgroep dat bedrijfswaardering als zelfstandige propositie in de markt zet, met een slimme samenwerkingstool voor adviseurs.',
    content: [
      {
        type: 'paragraph',
        content:
          'Mijn Bedrijfs Waarderingen is een nieuw, zelfstandig label binnen FCG Adviesgroep. Het richt zich op ondernemers die willen weten wat hun onderneming waard is, of dat nu gaat om een naderende verkoop, een overname, een aandeelhoudersgeschil of een interne overdracht binnen de familie. Sebastiaan Westmeijer staat als waarderingsdeskundige aan het roer.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'Binnen FCG Adviesgroep zat de bedrijfswaardering verstopt onder andere labels zoals Overnamehuys en Financieringsgilde. Ondernemers die specifiek op zoek waren naar een waardering kwamen daardoor zelden direct bij Sebastiaan terecht. Tegelijk wilde het label een platform worden waar meerdere adviseurs zich aan kunnen sluiten, zodat ondernemers in heel Noord-Holland en daarbuiten gekoppeld kunnen worden aan een specialist in hun regio.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Eigen merkidentiteit en domeinnaam opgezet, zodat Mijn Bedrijfs Waarderingen op eigen kracht zichtbaar wordt op de zoekterm bedrijfswaardering.',
          'Heldere sitestructuur rond vier situaties waarin een waardering nodig is: verkoop, overname, scheiding of conflict, en interne overdracht.',
          'Slimme samenwerkingstool gebouwd waarin adviseurs en Sebastiaan gezamenlijk waarderingstrajecten kunnen oppakken, zodat lokale kennis en specialistische expertise elkaar versterken.',
          'Hosting op de eigen Noord Digital omgeving zodat de techniek, snelheid en veiligheid op één lijn liggen met de andere FCG labels.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een nieuw label dat ondernemers met een concrete waarderingsvraag rechtstreeks bereikt, in plaats van via de omweg van een ander FCG merk.',
          'Versterking van de funnel naar de andere FCG labels, omdat een waarderingsklant vaak in een later stadium ook Overnamehuys of Financieringsgilde nodig heeft.',
          'Sebastiaan duidelijk gepositioneerd als waarderingsexpert in Noord-Holland, met een eigen digitaal platform om die positie te onderbouwen.',
          'Een groeiend samenwerkingsnetwerk waarin meerdere adviseurs aan kunnen sluiten, zonder dat ieder een eigen waarderingstraject vanaf nul hoeft op te bouwen.',
        ],
      },
      {
        type: 'callout',
        content:
          'Een nieuw label werkt alleen als het op eigen kracht zichtbaar wordt en tegelijk de rest van de groep versterkt. Dat is precies wat Mijn Bedrijfs Waarderingen doet.',
      },
      {
        type: 'paragraph',
        content:
          'FCG Adviesgroep heeft met Mijn Bedrijfs Waarderingen een label dat zelfstandig in de markt staat en tegelijk de funnel naar Overnamehuys en Financieringsgilde voedt, met Sebastiaan en de samenwerkingstool als motor.',
      },
    ],
    website: 'https://mijnbedrijfswaarderingen.nl',
  },
  {
    slug: 'flow-frl',
    clientName: 'Flow.frl',
    sector: 'Mediator',
    shortDescription:
      'Een rustige, professionele website voor FLOW Mediation die vertrouwen uitstraalt en zorgt voor een constante stroom aan nieuwe mediationaanvragen.',
    heroImage:
      'https://i0.wp.com/noorddigital.nl/wp-content/uploads/2026/03/WhatsApp-Image-2025-12-23-at-13.03.26.jpeg?fit=800%2C533&ssl=1',
    metaDescription:
      'Ontdek hoe wij FLOW Mediation hielpen aan een professionele website die vertrouwen wekt en structureel nieuwe aanvragen genereert.',
    content: [
      {
        type: 'paragraph',
        content:
          'FLOW Mediation & Arbeidsrecht begeleidt mensen en organisaties bij complexe conflicten en arbeidsrechtelijke vraagstukken. Hun aanpak kenmerkt zich door rust, deskundigheid en een oplossingsgerichte blik.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'Mensen die op zoek zijn naar een mediator bevinden zich vaak in een stressvolle situatie. De website moest daarom direct rust en vertrouwen uitstralen, zonder in te leveren op zakelijke professionaliteit. Daarnaast moest de vindbaarheid op specifieke mediation-termen in Friesland verbeterd worden.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Een kalm en overzichtelijk webdesign met zachte kleuren en heldere typografie.',
          'Duidelijke profilering van de expertise in zowel mediation als arbeidsrecht.',
          'Laagdrempelige contactmogelijkheden om de stap naar een eerste gesprek zo klein mogelijk te maken.',
          'Gerichte SEO-strategie op relevante zoektermen binnen de regio Friesland.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een professionele online uitstraling die direct het juiste, vertrouwde gevoel overbrengt.',
          'Een structurele toename in het aantal kwalitatieve aanvragen via de website.',
          'Betere organische vindbaarheid op cruciale zoektermen in de regio.',
        ],
      },
      {
        type: 'callout',
        content:
          'In stressvolle tijden is een heldere, rustige website de eerste stap naar een oplossing.',
      },
      {
        type: 'paragraph',
        content:
          'FLOW Mediation heeft nu een digitaal platform dat hun deskundigheid perfect weerspiegelt en actief bijdraagt aan de groei van de praktijk.',
      },
    ],
  },
  {
    slug: 'asfamily-fitness-center',
    clientName: 'Asfamily Fitness Center',
    sector: 'Sportschool',
    shortDescription:
      'Een volledig nieuwe, gebruiksvriendelijke website voor AS Family Fitness Center die het aanbod voor leden en nieuwe bezoekers overzichtelijk maakt.',
    heroImage:
      'https://i0.wp.com/noorddigital.nl/wp-content/uploads/2026/03/fitness-boven-2023-scaled-1.jpg?fit=800%2C450&ssl=1',
    metaDescription:
      'Ontdek hoe wij AS Family Fitness Center hielpen aan een moderne website die het aanbod overzichtelijk presenteert en nieuwe leden aantrekt.',
    content: [
      {
        type: 'paragraph',
        content:
          'AS Family Fitness Center is een moderne sportschool die zich richt op een brede doelgroep. Van fanatieke krachtsporters tot mensen die op zoek zijn naar groepslessen in een gezellige en ondersteunende omgeving.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'De oude website was verouderd, traag en niet geoptimaliseerd voor mobiele apparaten. Het was voor potentiële leden lastig om informatie te vinden over abonnementen, openingstijden en het lesrooster. Dit zorgde voor onnodig veel telefonische vragen en gemiste inschrijvingen.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Volledig redesign met een sportieve, energieke uitstraling die past bij de sfeer van de sportschool.',
          'Herstructurering van de informatie zodat abonnementen en roosters met één klik bereikbaar zijn.',
          'Integratie van duidelijke call-to-actions voor proeflessen en directe inschrijvingen.',
          'Lokale SEO-optimalisatie om beter gevonden te worden door mensen in de directe omgeving.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een significante stijging in het aantal online aanvragen voor proeflessen.',
          'Minder administratieve druk doordat leden zelf makkelijk informatie kunnen vinden.',
          'Een professionele online presentatie die de kwaliteit van de fysieke sportschool weerspiegelt.',
        ],
      },
      {
        type: 'callout',
        content:
          'Een website die net zo fit en energiek is als de leden van AS Family Fitness Center.',
      },
      {
        type: 'paragraph',
        content:
          'Door de drempel voor online inschrijvingen te verlagen, fungeert de nieuwe website nu als een effectieve motor voor de groei van het ledenbestand.',
      },
    ],
  },
  {
    slug: 'de-tuinvrouw',
    clientName: 'De Tuinvrouw',
    sector: 'Hovenier',
    shortDescription:
      'We vertaalden de missie van ecologisch tuinieren naar een inspirerende website die bezoekers uitnodigt om weer volop van hun tuin te genieten.',
    heroImage:
      'https://i0.wp.com/noorddigital.nl/wp-content/uploads/2026/03/WH-20251104-0001-kopie-scaled.jpg?fit=800%2C523&ssl=1',
    metaDescription:
      'Lees de case over De Tuinvrouw, waarvoor wij een inspirerende website ontwikkelden gericht op ecologisch tuinieren en lokaal bereik.',
    content: [
      {
        type: 'paragraph',
        content:
          'De Tuinvrouw is een gepassioneerde hovenier met een duidelijke missie: ecologisch tuinieren toegankelijk maken en mensen helpen om weer echt te genieten van hun buitenruimte.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'De visie op ecologisch en duurzaam tuinieren kwam niet goed naar voren in de online communicatie. Er was behoefte aan een platform dat niet alleen diensten aanbiedt, maar ook inspireert en de specifieke, natuurvriendelijke aanpak helder uitlegt aan potentiële klanten.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Ontwikkeling van een visueel aantrekkelijk design met veel ruimte voor hoogwaardige fotografie van gerealiseerde tuinen.',
          'Focus op storytelling om de ecologische missie en persoonlijke werkwijze over te brengen.',
          'Opzetten van een overzichtelijke dienstenstructuur (ontwerp, aanleg, onderhoud).',
          'Lokale vindbaarheid verbeterd zodat mensen in de regio gericht op zoek kunnen naar een duurzame hovenier.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een website die perfect aansluit bij de groene, duurzame identiteit van het bedrijf.',
          'Kwalitatievere aanvragen van klanten die specifiek op zoek zijn naar een ecologische aanpak.',
          'Een platform dat dient als inspiratiebron en portfolio tegelijk.',
        ],
      },
      {
        type: 'callout',
        content:
          'Een digitaal visitekaartje dat net zo organisch en doordacht is als de tuinen die ze ontwerpen.',
      },
      {
        type: 'paragraph',
        content:
          'De nieuwe website helpt De Tuinvrouw om de juiste klanten aan te trekken die haar visie op natuurvriendelijk tuinieren delen.',
      },
    ],
  },
  {
    slug: 'steenhuisvisuals',
    clientName: 'SteenhuisVisuals',
    sector: 'Nieuwsfotograaf',
    shortDescription:
      'Een krachtig nieuwsplatform dat de reportages van SteenhuisVisuals centraal zet en hem als zelfstandig nieuwsfotograaf sterk positioneert.',
    heroImage:
      'https://i0.wp.com/noorddigital.nl/wp-content/uploads/2025/12/Steekpartij_Van_Houtenlaan-28-scaled.jpg?fit=800%2C534&ssl=1',
    metaDescription:
      'Lees hoe wij voor SteenhuisVisuals een beeldbedreven platform ontwikkelden dat zijn werk als nieuwsfotograaf optimaal presenteert.',
    content: [
      {
        type: 'paragraph',
        content:
          'SteenhuisVisuals is een onafhankelijke nieuwsfotograaf die 24/7 klaarstaat om actueel nieuws vast te leggen. Zijn beelden worden gebruikt door diverse regionale en landelijke media.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          "Als nieuwsfotograaf is snelheid cruciaal, maar ook de presentatie van het werk. De oude website was traag en legde niet de juiste nadruk op de krachtige beelden. Er was een platform nodig dat grote hoeveelheden hoge resolutie foto's snel kon laden en overzichtelijk kon presenteren aan redacties.",
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Een "beeld-eerst" design waarbij de interface minimaal is en de foto\'s de volledige aandacht krijgen.',
          'Technische optimalisatie voor razendsnelle laadtijden, zelfs bij zware fotogalerijen.',
          'Een logische categorisering van nieuwsitems (112-nieuws, evenementen, portretten).',
          'Integratie van snelle contactopties voor redacties die direct beeldmateriaal nodig hebben.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een bliksemsnelle website die het werk van de fotograaf eer aandoet.',
          'Een professioneel platform dat als betrouwbare bron dient voor mediapartners.',
          'Eenvoudig beheer aan de achterkant, zodat nieuw beeldmateriaal direct gepubliceerd kan worden.',
        ],
      },
      {
        type: 'callout',
        content:
          'Wanneer nieuws niet wacht, moet een website direct presteren.',
      },
      {
        type: 'paragraph',
        content:
          'SteenhuisVisuals heeft nu een robuust platform dat zijn positie als snelle en betrouwbare nieuwsfotograaf in de regio versterkt.',
      },
    ],
  },
  {
    slug: 'tommys-services-security',
    clientName: "Tommy's Services & Security",
    sector: 'Beveiliger',
    shortDescription:
      "Een modern platform voor Tommy's Services & Security dat de professionele dienstverlening zichtbaar maakt en een sterke online positie creëert.",
    heroImage:
      'https://i0.wp.com/noorddigital.nl/wp-content/uploads/2025/12/9F978C62-F27E-4ADF-A5BA-5AB04B76A8C5.jpeg?fit=800%2C1000&ssl=1',
    metaDescription:
      "Ontdek hoe wij Tommy's Services & Security hielpen aan een professionele website die autoriteit en betrouwbaarheid uitstraalt.",
    content: [
      {
        type: 'paragraph',
        content:
          "Tommy's Services & Security levert hoogwaardige beveiligingsdiensten voor evenementen, objecten en persoonsbeveiliging. Betrouwbaarheid, discretie en professionaliteit staan centraal in hun dienstverlening.",
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          "In de beveiligingsbranche is vertrouwen alles. De online aanwezigheid van Tommy's weerspiegelde nog niet de hoge standaard van hun werk in de praktijk. Ze hadden een platform nodig dat autoriteit uitstraalt en grote opdrachtgevers overtuigt van hun capaciteiten.",
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Een strak, donker en modern webdesign dat direct autoriteit en veiligheid communiceert.',
          "Heldere structurering van de verschillende beveiligingsdiensten met specifieke landingspagina's.",
          'Toevoegen van keurmerken en certificeringen om direct vertrouwen op te bouwen.',
          'Optimalisatie van conversiepaden voor zakelijke aanvragen en offertes.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een sterke online positionering die past bij een premium beveiligingsbedrijf.',
          'Meer kwalitatieve aanvragen van zakelijke opdrachtgevers en evenementenorganisaties.',
          'Een schaalbaar platform dat meegroeit met de ambities van het bedrijf.',
        ],
      },
      {
        type: 'callout',
        content:
          'Vertrouwen begint bij de eerste indruk. Online is dat je website.',
      },
      {
        type: 'paragraph',
        content:
          "Met de nieuwe website heeft Tommy's Services & Security een digitaal visitekaartje dat deuren opent bij nieuwe, grotere opdrachtgevers.",
      },
    ],
  },
  {
    slug: 'mynt-schoonmaak',
    clientName: 'Mynt Schoonmaak',
    sector: 'Schoonmaker',
    shortDescription:
      'Een betrouwbaar platform voor Mynt Schoonmaak dat zorgde voor een ommekeer en nu een constante stroom aan nieuwe schoonmaakklanten oplevert.',
    heroImage:
      'https://i0.wp.com/noorddigital.nl/wp-content/uploads/2025/11/clearning-lady-2.jpg?fit=800%2C534&ssl=1',
    metaDescription:
      'Lees hoe wij Mynt Schoonmaak hielpen aan een conversiegerichte website die lokaal domineert en structureel nieuwe klanten aantrekt.',
    content: [
      {
        type: 'paragraph',
        content:
          'Mynt Schoonmaak is een professioneel schoonmaakbedrijf dat zich richt op zowel de zakelijke als particuliere markt. Ze staan bekend om hun grondige aanpak en betrouwbare service.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'Het bedrijf bevond zich in een uitdagende fase en had dringend behoefte aan een voorspelbare stroom van nieuwe klanten. De bestaande website genereerde vrijwel geen leads en was niet goed vindbaar in de regio op relevante zoektermen.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Een frisse, schone uitstraling van de website die de kernwaarde van het bedrijf weerspiegelt.',
          'Implementatie van een sterke lokale SEO-strategie gericht op specifieke diensten (kantoorschoonmaak, opleveringsschoonmaak).',
          'Duidelijke en snelle offerte-formulieren om de conversiedrempel te verlagen.',
          'Opzetten van een review-systeem om sociaal bewijs te tonen aan nieuwe bezoekers.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een succesvolle ommekeer voor het bedrijf door een constante stroom aan nieuwe aanvragen.',
          'Top-posities in Google voor de belangrijkste lokale zoektermen.',
          'Een professioneel platform dat het vertrouwen van zakelijke opdrachtgevers wint.',
        ],
      },
      {
        type: 'callout',
        content:
          'Een goede website is geen kostenpost, maar de beste salesmedewerker die je kunt hebben.',
      },
      {
        type: 'paragraph',
        content:
          'Mynt Schoonmaak is nu niet meer afhankelijk van mond-tot-mondreclame, maar heeft een voorspelbaar systeem voor online klantacquisitie.',
      },
    ],
  },
  {
    slug: 'met-carla',
    clientName: 'Met Carla',
    sector: 'Uitvaartzorg',
    shortDescription:
      'Een warm en betekenisvol platform voor ritueelbegeleider Met Carla, compleet met visuele symboliek die haar persoonlijke begeleiding weerspiegelt.',
    heroImage:
      'https://i0.wp.com/noorddigital.nl/wp-content/uploads/2025/05/image_2025-05-23_125549813.png?fit=800%2C800&ssl=1',
    metaDescription:
      'Ontdek hoe wij voor Met Carla een warme, respectvolle website ontwierpen die haar rol als ritueelbegeleider perfect ondersteunt.',
    content: [
      {
        type: 'paragraph',
        content:
          'Carla is werkzaam als ritueelbegeleider, uitvaartspreker en tekstschrijver. Ze helpt nabestaanden om op een persoonlijke en betekenisvolle manier afscheid te nemen van hun dierbaren.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'In de uitvaartzorg draait alles om gevoel, empathie en vertrouwen. De website moest deze delicate balans perfect weten te raken. Het mocht niet te zakelijk zijn, maar moest wel duidelijk communiceren wat Carla voor mensen in een kwetsbare periode kan betekenen.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Ontwerp van een warme, zachte interface met zorgvuldig gekozen kleuren en typografie.',
          'Integratie van subtiele animaties, zoals een zacht vallende veer, als symbool voor het rouwproces en haar begeleiding.',
          'Ruimte voor persoonlijke verhalen en haar eigen achtergrond om direct een connectie te maken met de bezoeker.',
          'Een heldere maar respectvolle manier om contact op te nemen in tijden van verlies.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een prachtig, betekenisvol platform dat exact de juiste toon aanslaat.',
          'Bezoekers voelen zich direct op hun gemak en begrepen door de warme uitstraling.',
          "Een online plek die Carla's unieke en persoonlijke benadering van afscheid nemen eer aandoet.",
        ],
      },
      {
        type: 'callout',
        content: 'Techniek en design in dienst van menselijkheid en emotie.',
      },
      {
        type: 'paragraph',
        content:
          'De website van Met Carla is een mooi voorbeeld van hoe digitaal design kan bijdragen aan een warm en menselijk proces.',
      },
    ],
  },
  {
    slug: 'common-frame-media',
    clientName: 'Common Frame Media',
    sector: 'Productie Assistentie',
    shortDescription:
      'Een strakke, filmische one-pager voor Common Frame Media die perfect aansluit bij hun internationale uitstraling en creatieve identiteit.',
    heroImage:
      'https://i0.wp.com/noorddigital.nl/wp-content/uploads/2025/11/3c184547-8a12-4690-a528-ab415da58f0a.jpg?fit=800%2C600&ssl=1',
    metaDescription:
      'Lees hoe wij voor Common Frame Media een filmische, Engelstalige one-pager ontwikkelden die hun creatieve werk centraal stelt.',
    content: [
      {
        type: 'paragraph',
        content:
          'Common Frame Media (CFM) is een creatief bureau gespecialiseerd in productie-assistentie en media-ontwikkeling. Ze werken aan diverse (internationale) projecten in de film- en media-industrie.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'Als creatief bureau hadden ze al sterke visuele ideeën voor hun online identiteit. De uitdaging was om deze ideeën te vertalen naar een functionele, snelle en professionele website die hun internationale portfolio op de juiste manier presenteert zonder overladen te voelen.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Ontwikkeling van een vloeiende, Engelstalige one-pager die bezoekers door hun verhaal leidt.',
          'Een filmisch design met grote beelden en video-elementen die direct de aandacht grijpen.',
          'Technische optimalisatie om ervoor te zorgen dat de zware media-bestanden snel laden op alle apparaten.',
          'Naadloze integratie van hun bestaande creatieve concepten met best practices in webdesign.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een indrukwekkend digitaal portfolio dat hun creatieve identiteit direct voelbaar maakt.',
          'Een strakke, internationale uitstraling die past bij hun ambities en doelgroep.',
          'Een website die visueel verbluft maar technisch feilloos presteert.',
        ],
      },
      {
        type: 'callout',
        content:
          'Een website voor videomakers moet net zo strak geregisseerd zijn als hun eigen producties.',
      },
      {
        type: 'paragraph',
        content:
          'Common Frame Media beschikt nu over een krachtig platform om hun werk te presenteren aan internationale opdrachtgevers en partners.',
      },
    ],
  },
  {
    slug: 'jaepie-jaepie',
    clientName: 'Jaepie Jaepie',
    sector: 'Foodtruck',
    shortDescription:
      'Maatwerk voor de website van Jaepie-Jaepie met een speelse en herkenbare uitstraling die bezoekers direct in de sfeer van de foodtruck brengt.',
    heroImage:
      'https://i0.wp.com/noorddigital.nl/wp-content/uploads/2025/04/image_2025-04-13_160331737.png?fit=245%2C206&ssl=1',
    metaDescription:
      'Ontdek hoe wij de website van foodtruck Jaepie-Jaepie voorzagen van speels maatwerk dat perfect past bij hun unieke concept.',
    content: [
      {
        type: 'paragraph',
        content:
          'Jaepie-Jaepie is een unieke, sfeervolle foodtruck die op evenementen, feesten en partijen zorgt voor heerlijk eten en een onvergetelijke beleving.',
      },
      { type: 'heading', content: 'De uitdaging' },
      {
        type: 'paragraph',
        content:
          'De eigenaar, Pim, had een duidelijke visie voor zijn foodtruck, maar de website miste de speelse en unieke sfeer die de fysieke truck wel had. Er was behoefte aan maatwerk om de online ervaring naadloos te laten aansluiten bij het concept.',
      },
      { type: 'heading', content: 'Onze aanpak' },
      {
        type: 'list',
        items: [
          'Toevoegen van speelse, op maat gemaakte designelementen die de sfeer van de foodtruck weerspiegelen.',
          'Optimalisatie van de menukaart en boekingsmogelijkheden voor evenementen.',
          'Een visuele stijl die vrolijkheid en gastvrijheid uitstraalt, passend bij de huisstijl.',
          'Verbetering van de mobiele weergave, aangezien veel aanvragen via smartphones worden gedaan.',
        ],
      },
      { type: 'heading', content: 'Het resultaat' },
      {
        type: 'list',
        items: [
          'Een herkenbare en sfeervolle website die bezoekers direct in de juiste stemming brengt.',
          'Een toename in het aantal aanvragen voor feesten en partijen.',
          'Een online presentatie waar de eigenaar net zo trots op is als op zijn foodtruck zelf.',
        ],
      },
      {
        type: 'callout',
        content:
          'De online beleving moet net zo smaakvol zijn als het eten dat je serveert.',
      },
      {
        type: 'paragraph',
        content:
          'Met deze maatwerk aanpassingen heeft Jaepie-Jaepie een website die de unieke charme van het concept perfect digitaal vertaalt.',
      },
    ],
  },
]

export const getClientCaseBySlug = (slug: string): ClientCase | undefined => {
  return clientCases.find((c) => c.slug === slug)
}
